import jwt_decode from 'jwt-decode';
import qs from 'qs';
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { getCookie, setCookieValue } from '../lib/cookie';
import { AUTH_URL, isDevelopment } from '../lib/environment';
import { sanitizeValue } from '../lib/sanitize';
import { useNotification } from './NotificationContext';
import { addHoursToDate } from '../lib/dateHelpers';

type Action =
  | { type: 'login'; accessToken: string; refreshToken: string }
  | { type: 'logout' };

type State = {
  accessToken: string | undefined;
  loggedIn: boolean;
  refreshToken: string | undefined;
  user: UserToken | undefined;
};

const clientId = '74bdf83936c6408bba4f4510a232c9d1';
const clientSecret = '8Sk_VO7ocLgQIO6QpO0Q6Ke1JwXC9WrwtuZ7Mww_d_s';
const UserContext = createContext<UserContext | undefined>(undefined);
const initialState = {
  accessToken: undefined,
  loggedIn: false,
  refreshToken: undefined,
  user: undefined,
  isInternal: false,
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'login': {
      return {
        accessToken: action.accessToken,
        loggedIn: true,
        refreshToken: action.refreshToken,
        user: jwt_decode(action.accessToken) as UserToken,
      };
    }
    case 'logout': {
      return initialState;
    }
    default: {
      return state;
    }
  }
}

export function UserProvider(props: UserProviderProps) {
  const { children } = props;
  const { addNotification } = useNotification();
  const tokenName = isDevelopment() ? 'devToken' : 'token';
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    makeTokenRequest(getTokenData());
  }, []);

  useEffect(() => {
    window.addEventListener('focus', handleWindowFocus);
    function handleWindowFocus() {
      if (!state.accessToken) return;
      const accessToken = jwt_decode(state.accessToken);
      const expiration = new Date((accessToken as UserToken).exp * 1000);

      if (
        addHoursToDate(new Date(expiration), -2).getTime() <=
        new Date().getTime()
      ) {
        makeTokenRequest(getTokenData());
      }
    }
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [state.accessToken]);

  function hasRole(role: string) {
    return (
      state.user !== undefined &&
      state.user.role.findIndex((r) => r.substring(0, role.length) === role) >
        -1
    );
  }

  function getTokenData() {
    const cookie = getCookie();
    const token = cookie[tokenName];

    if (!token) return;

    setIsRefreshing(true);
    return qs.stringify({
      grant_type: 'refresh_token',
      scope: 'offline_access',
      refresh_token: token,
      client_id: clientId,
      client_secret: clientSecret,
    });
  }

  function login(
    username: string,
    password: string,
    callback: (success: boolean) => any
  ) {
    let data = qs.stringify({
      grant_type: 'password',
      username: username,
      password: password,
      scope: 'offline_access',
      client_id: clientId,
      client_secret: clientSecret,
    });

    makeTokenRequest(data, callback);
  }

  function makeTokenRequest(
    data?: string,
    callback?: (success: boolean) => any
  ) {
    if (!data) return;

    setLoading(true);

    fetch(`${AUTH_URL}/User/Login`, {
      body: data,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      method: 'POST',
    })
      .then(async (res) => {
        if (res.status >= 400) {
          await Promise.reject(await getResponseError(res));
        }

        const data = await res.json();

        setCookieValue(tokenName, data.refresh_token);
        setError(null);

        dispatch({
          type: 'login',
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });

        if (callback) callback(res.status === 200);
      })
      .catch((err) => {
        console.log(err);
        setError(err.response);
        logout();

        addNotification({
          dismissible: true,
          message:
            typeof err.response.data === 'object'
              ? err.response.data.error_description
              : err.response.data,
          name: `Status Code: ${err.response.status}`,
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
        setIsRefreshing(false);
      });
  }

  function resetPassword(username: string) {
    setLoading(true);

    fetch(`${AUTH_URL}/User/RequestPasswordReset`, {
      body: JSON.stringify(
        sanitizeValue({
          Email: username,
          Redirect: origin,
        })
      ),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(async (res) => {
        if (res.status >= 400) {
          await Promise.reject(await getResponseError(res));
        }

        if (res.status === 200) {
          addNotification({
            dismissible: true,
            message: `Password reset sent to ${username}`,
            name: 'Email Sent',
            type: 'info',
          });
        }
        setError(null);
      })
      .catch((err) => {
        console.log(err.response);
        setError(err.response);
        addNotification({
          dismissible: true,
          message: err.response.data,
          name: `Status Code: ${err.response.status}`,
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function changePassword(
    currentPassword: string,
    newPassword: string,
    newConfirm: string
  ) {
    if (state.user) {
      setLoading(true);

      fetch(`${AUTH_URL}/User/SetNewPassword`, {
        body: JSON.stringify(
          sanitizeValue({
            Email: state.user.unique_name,
            CurrentPassword: currentPassword,
            Password: newPassword,
            ConfirmPassword: newConfirm,
          })
        ),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
        .then(async (res) => {
          if (res.status >= 400) {
            await Promise.reject(await getResponseError(res));
          }

          if (res.status === 200) {
            addNotification({
              dismissible: true,
              message: `Your password has been updated`,
              name: 'Password Changed',
              type: 'info',
            });
          }
          setError(null);
        })
        .catch((err) => {
          console.log(err.response);
          setError(err.response);
          addNotification({
            dismissible: true,
            message: 'There was an error updating Password',
            name: `Status Code: ${err.response.status}`,
            type: 'error',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function logout() {
    setCookieValue(tokenName, null);
    dispatch({ type: 'logout' });
  }

  return (
    <UserContext.Provider
      value={{
        email: state.user?.unique_name,
        error: error,
        changePassword: changePassword,
        isRefreshing: isRefreshing,
        isInternal:
          state.user?.unique_name?.endsWith('tangerineglobal.com') ?? false,
        loading: loading,
        loggedIn: state.loggedIn,
        login: login,
        logout: logout,
        permissions: buildPermissions(hasRole),
        resetPassword: resetPassword,
        token: state.accessToken ? `bearer ${state.accessToken}` : undefined,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

type ResponseError = {
  response: {
    data: object | string;
    status: number;
  };
};

const getResponseError = async (response: Response): Promise<ResponseError> => {
  const data = await response
    .clone()
    .json()
    .catch(async () => await response.clone().text());

  return Promise.resolve({
    response: {
      data,
      status: response.status,
    },
  });
};

export function useUser() {
  const context = useContext(UserContext);

  if (context === undefined)
    throw new Error('useUser must be used within a UserProvider');

  return context;
}

const buildPermissions = (hasRole: (role: string) => boolean) => ({
  canAdd: hasRole('ResAcctAdddevice'),
  canCDVR: hasRole('ResAcctCloudDvr'),
  canChannel: hasRole('ResDvcChannels'),
  canClearAdult: hasRole('ResAcctClearAdultOpt'),
  canDevices: hasRole('HosZControlDevices'),
  canFips: hasRole('ResAcctFips'),
  canGroup: hasRole('HosZControlGroup'),
  canGuestName: hasRole('HosZControlGuestName'),
  canHistory: hasRole('HosZControlHistory'),
  canHos: hasRole('Hos'),
  canHosAccountDServerAnalytics: hasRole('HosAccountDServerAnalytics'),
  canHosAcknowledgeSencore: hasRole('HosZSencoreAcknowledge'),
  canHosAdScheduler: hasRole('HosAdScheduler'),
  canHosAnalytics: hasRole('HosAnalytics'),
  canHosBulk: hasRole('HosControlBulk'),
  canHosCitrusView: hasRole('HosCitrusView'),
  canHosConfigureSencore: hasRole('HosZSencoreConfigure'),
  canHosConfigureSimulator: hasRole('HosZControlSimulator'),
  canHosControlGroup: hasRole('HosZControlGroup'),
  canHosControlLateCheckout: hasRole('HosZControlLateCheckout'),
  canHosControlPackages: hasRole('HosZControlPackages'),
  canHosControlProperty: hasRole('HosControlProperty'),
  canHosControlSite: hasRole('HosZControlSite'),
  canHosControlStatus: hasRole('HosZControlStatus'),
  canHosControlTransport: hasRole('HosZControlTransport'),
  canHosControlType: hasRole('HosZControlType'),
  canHosControlVod: hasRole('HosZControlVod'),
  canHosCreditLimit: hasRole('HosZControlCreditLimit'),
  canHosCreditReset: hasRole('HosZControlCreditReset'),
  canHosDServerAnalytics: hasRole('HosDServerAnalytics'),
  canHosSubnetDServerAnalytics: hasRole('HosSubnetDServerAnalytics'),
  canHosDServerTroubleshooting: hasRole('HosDServerTroubleshooting'),
  canHosDVR: hasRole('HosZControlDvr'),
  canHosDsFormBuilder: hasRole('HosSignageFormbuilder'),
  canHosDsQuicklink: hasRole('HosSignageQuicklink'),
  canHosDsScheduler: hasRole('HosSignageScheduler'),
  canHosGlobalView: hasRole('HosZGlobalView'),
  canHosInventory: hasRole('HosControlInventory'),
  canHosInfrastructure: hasRole('HosZInfrastructureInventory'),
  canHosLateCheckoutConfig: hasRole('HosLatecheckoutConfig'),
  canHosLateCheckoutView: hasRole('HosLatecheckoutView'),
  canHosMenuErrors: hasRole('HosMenuErrors'),
  canHosMenuHits: hasRole('HosMenuHits'),
  canHosMenuSearch: hasRole('HosMenuSearch'),
  canHosMenuTesting: hasRole('HosMenuTesting'),
  canHosMenuTranslations: hasRole('HosMenuTranslations'),
  canHosMessagingApprove: hasRole('HosMessagingApprove'),
  canHosMessagingCreate: hasRole('HosMessagingCreate'),
  canHosMessagingView: hasRole('HosMessagingView'),
  canHosPINs: hasRole('HosZControlPins'),
  canHosPmsSimulator: hasRole('HosPmsSimulator'),
  canHosProvision: hasRole('HosControlProvision'),
  canHosRDPView: hasRole('HosZRDPView'),
  canHosRoom: hasRole('HosControlRoom'),
  canHosSsidEvents: hasRole('HosSsidEvents'),
  canHosSsidViewer: hasRole('HosSsidViewer'),
  canHosSsidScheduler: hasRole('HosSsidScheduler'),
  canHosStatus: hasRole('HosZControlStatus'),
  canHosSupportCreate: hasRole('HosSupportCreate'),
  canHosSupportView: hasRole('HosSupportView'),
  canHosSystems: hasRole('HosSystemPage'),
  canHosTransport: hasRole('HosZControlTransport'),
  canHosViewSencore: hasRole('HosZSencore'),
  canHosWifiOverview: hasRole('HosZWifiOverview'),
  canIcc: hasRole('ResAcctIcc'),
  canLC: hasRole('HosZControlLateCheckout'),
  canLaunch: hasRole('ResDvcLaunchPage'),
  canLocation: hasRole('HosZControlLocations'),
  canName: hasRole('ResAcctName'),
  canPMSMessages: hasRole('HosZControlQueueMessages'),
  canPackages: hasRole('HosZControlPackages'),
  canRemove: hasRole('ResDvcRemove'),
  canRes: hasRole('Res'),
  canResAccountDServerAnalytics: hasRole('ResAccountDServerAnalytics'),
  canResAcctAdddevice: hasRole('ResAcctAdddevice'),
  canResAcctClearAdultOpt: hasRole('ResAcctClearAdultOpt'),
  canResAcctCloudDvr: hasRole('ResAcctCloudDvr'),
  canResAcctCreate: hasRole('ResAcctCreate'),
  canResAcctCreditlimit: hasRole('ResAcctCreditlimit'),
  canResAcctCreditreset: hasRole('ResAcctCreditreset'),
  canResAcctDisable: hasRole('ResAcctDisable'),
  canResAcctDvr: hasRole('ResAcctDvr'),
  canResAcctFips: hasRole('ResAcctFips'),
  canResAcctIcc: hasRole('ResAcctIcc'),
  canResAcctMfgroup: hasRole('ResAcctMfgroup'),
  canResAcctName: hasRole('ResAcctName'),
  canResAcctPkgchange: hasRole('ResAcctPkgchange'),
  canResAcctResetpins: hasRole('ResAcctResetpins'),
  canResAcctScreensaver: hasRole('ResAcctScreensaver'),
  canResAcctTransport: hasRole('ResAcctTransport'),
  canResAcknowledgeSencore: hasRole('ResZSencoreAcknowledge'),
  canResAnalytics: hasRole('ResAnalytics'),
  canResCitrusView: hasRole('ResZCitrusView'),
  canResConfigureSencore: hasRole('ResZSencoreConfigure'),
  canResCreditLimit: hasRole('ResAcctCreditlimit'),
  canResCreditReset: hasRole('ResAcctCreditreset'),
  canResDServerAnalytics: hasRole('ResDServerAnalytics'),
  canResSubnetDServerAnalytics: hasRole('ResSubnetDServerAnalytics'),
  canResDServerTroubleshooting: hasRole('ResDServerTroubleshooting'),
  canResDVR: hasRole('ResAcctDvr'),
  canResDvcChannels: hasRole('ResDvcChannels'),
  canResDvcLaunchPage: hasRole('ResDvcLaunchPage'),
  canResDvcReboot: hasRole('ResDvcReboot'),
  canResGlobalView: hasRole('ResZGlobalView'),
  canResInventory: hasRole('ResOpInventory'),
  canResInfrastructure: hasRole('ResZInfrastructureInventory'),
  canResInventoryDelete: hasRole('ResOpInventoryDelete'),
  canResMessagingCreate: hasRole('ResZMessagingCreate'),
  canResMessagingView: hasRole('ResZMessagingView'),
  canResOpAppkeys: hasRole('ResOpAppkeys'),
  canResOpBillingSkus: hasRole('ResOpBillingSkus'),
  canResOpChannelMapGroups: hasRole('ResOpChannelMapGroups'),
  canResOpCloudDvrs: hasRole('ResOpCloudDvrs'),
  canResOpContacts: hasRole('ResOpContacts'),
  canResOpCreate: hasRole('ResOpCreate'),
  canResOpCreditLimit: hasRole('ResOpCreditlimit'),
  canResOpExperienceGroups: hasRole('ResOpExperienceGroups'),
  canResOpFips: hasRole('ResOpFips'),
  canResOpInventory: hasRole('ResOpInventory'),
  canResOpInventoryDelete: hasRole('ResOpInventoryDelete'),
  canResOpIpAccounts: hasRole('ResOpIpaccounts'),
  canResOpIpBlock: hasRole('ResOpIpblock'),
  canResOpModels: hasRole('ResOpModels'),
  canResOpPackages: hasRole('ResOpPackages'),
  canResOpRoutingGroups: hasRole('ResOpRoutingGroups'),
  canResOpTransport: hasRole('ResOpTransport'),
  canResPINs: hasRole('ResAcctResetpins'),
  canResPackages: hasRole('ResOpPackages'),
  canResRDPView: hasRole('ResZRDPView'),
  canResStatus: hasRole('ResAcctDisable'),
  canResSupportCreate: hasRole('ResZSupportCreate'),
  canResSupportView: hasRole('ResZSupportView'),
  canResTransport: hasRole('ResAcctTransport'),
  canResViewSencore: hasRole('ResZSencore'),
  canRoles: hasRole('UsrRoles'),
  canScreensaver: hasRole('ResAcctScreensaver'),
  canSite: hasRole('HosZControlSite'),
  canType: hasRole('HosZControlType'),
  canUsers: hasRole('UsrUsers'),
  canVOD: hasRole('HosZControlVod'),
  isSuperUser: hasRole('SuperUser'),
});
